/**
 * 返修工单列表页
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Input, Select, Button, Card, Row, Col, Modal, Form, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { returnApi } from '../../services/api';

const statusMap = { pending: '待处理', sent: '已寄出', processing: '处理中', completed: '已办结', cancelled: '已取消' };
const statusColors = { pending: 'orange', sent: 'blue', processing: 'processing', completed: 'success', cancelled: 'error' };

function ReturnList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: '', keyword: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const navigate = useNavigate();

  const columns = [
    { title: '返修单号', dataIndex: 'return_no', width: 170 },
    { title: '客户', dataIndex: 'customer_name', width: 100 },
    { title: '电话', dataIndex: 'phone', width: 120 },
    { title: '供应商', dataIndex: 'supplier', width: 130, ellipsis: true },
    { title: '故障描述', dataIndex: 'fault_desc', width: 180, ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 100, render: (s) => <Tag color={statusColors[s]}>{statusMap[s] || s}</Tag> },
    { title: '提醒次数', dataIndex: 'remind_count', width: 80, render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    { title: '操作', width: 80, fixed: 'right', render: (_, r) => <Button type="link" onClick={() => navigate(`/return/detail/${r.id}`)}>详情</Button> },
  ];

  const loadData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await returnApi.list({ page, pageSize, ...filters });
      setData(res.data?.list || []);
      setPagination({ page: res.data?.pagination?.page || 1, pageSize: res.data?.pagination?.pageSize || 20, total: res.data?.pagination?.total || 0 });
    } catch (err) { } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      await returnApi.create(values);
      message.success('返修工单创建成功');
      setCreateOpen(false);
      createForm.resetFields();
      loadData();
    } catch (err) {
      // 已在拦截器处理
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col><Input placeholder="搜索单号/客户/供应商" prefix={<SearchOutlined />} value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })} onPressEnter={() => loadData()} style={{ width: 250 }} allowClear /></Col>
          <Col>
            <Select placeholder="状态" value={filters.status || undefined} allowClear style={{ width: 140 }}
              onChange={v => setFilters({ ...filters, status: v || '' })}>
              {Object.entries(statusMap).map(([k, v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}
            </Select>
          </Col>
          <Col><Button type="primary" icon={<SearchOutlined />} onClick={() => loadData()}>搜索</Button></Col>
          <Col><Button icon={<ReloadOutlined />} onClick={() => { setFilters({ status: '', keyword: '' }); setTimeout(() => loadData(), 0); }}>重置</Button></Col>
          <Col flex="auto" />
          <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>新建返修</Button></Col>
        </Row>
      </Card>
      <Table rowKey="id" columns={columns} dataSource={data} loading={loading}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`, onChange: (page, pageSize) => loadData(page, pageSize) }}
        scroll={{ x: 1200 }} />

      <Modal
        title="新建返修工单"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        confirmLoading={createLoading}
        okText="创建"
        cancelText="取消"
        width={640}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户姓名" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="return_address" label="回寄地址" rules={[{ required: true, message: '请输入回寄地址' }]}>
            <Input placeholder="客户寄回物品的地址" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact_person" label="联系人" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sn_number" label="SN/机身号">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="fault_desc" label="故障描述" rules={[{ required: true, message: '请描述故障现象' }]}>
            <Input.TextArea rows={3} placeholder="请描述设备故障现象" />
          </Form.Item>
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginBottom: 8, fontWeight: 500, color: '#1677ff' }}>
            供应商信息
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier" label="供应商名称" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier_phone" label="供应商电话" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier_address" label="寄往地址" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="供应商收货地址" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier_contact" label="供应商联系人" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_express_company" label="快递公司（如有）">
                <Input placeholder="如：顺丰" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_express_no" label="快递单号（如有）">
                <Input placeholder="已寄出快递单号" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}

export default ReturnList;
